import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { MaskedInput } from '@/components/form/masked-input';
import { Checkbox } from '@/components/ui/checkbox';
import { Field } from '@/components/form/field';
import { FormError } from '@/components/form/form-error';
import { useZodForm } from '@/components/form/use-zod-form';
import { Button } from '@/components/ui/button';
import { Item } from '@/components/ui/item';
import type { TwoFactorSecret } from '../api/auth';
import { totpCode } from '../schemas';

const schema = z.object({ code: totpCode });

/**
 * QR Code + código de confirmação, usado na configuração obrigatória do login e na ativação
 * opcional em "Minha conta". O QR é gerado no navegador: o segredo não sai para serviço
 * nenhum de terceiro.
 */
export function TwoFactorEnrollment({
  secret,
  onConfirm,
}: {
  secret: TwoFactorSecret;
  onConfirm: (code: string) => Promise<void>;
}) {
  const [qr, setQr] = useState<string | null>(null);
  const form = useZodForm(schema, { code: '' });
  const [serverError, setServerError] = useState<unknown>(null);

  useEffect(() => {
    void QRCode.toDataURL(secret.otpauthUri, { margin: 1, width: 200 }).then(setQr);
  }, [secret.otpauthUri]);

  const onSubmit = form.handleSubmit(async ({ code }) => {
    setServerError(null);
    try {
      await onConfirm(code);
    } catch (caught) {
      setServerError(caught);
    }
  });

  return (
    <div className="space-y-4">
      <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
        <li>Abra o aplicativo autenticador (Google Authenticator, Microsoft Authenticator…).</li>
        <li>Leia o QR Code abaixo ou digite a chave manualmente.</li>
        <li>Informe o código de 6 dígitos que aparecer.</li>
      </ol>

      {/* Fundo branco literal de propósito: QR Code precisa de contraste fixo para a câmera
          ler, independentemente do tema. */}
      <div className="flex justify-center rounded-md border bg-white p-3">
        {qr ? (
          <img src={qr} alt="QR Code para o aplicativo autenticador" className="size-48" />
        ) : (
          <div className="size-48" aria-busy="true" />
        )}
      </div>

      <p className="break-all text-center font-mono text-xs text-muted-foreground">
        {secret.secret}
      </p>

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <FormError error={serverError} fallback="Não foi possível confirmar o código." />
        <Field control={form.control} name="code" label="Código do aplicativo" required>
          {(field) => <MaskedInput {...field} mask="code" autoComplete="one-time-code" />}
        </Field>
        <Button type="submit" className="w-full" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? 'Confirmando…' : 'Confirmar'}
        </Button>
      </form>
    </div>
  );
}

/**
 * Códigos de recuperação, mostrados uma única vez. O botão só libera depois da confirmação
 * de que foram guardados: sem eles, perder o celular é perder a conta.
 */
export function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const [saved, setSaved] = useState(false);

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Guarde estes códigos em lugar seguro. Cada um entra uma única vez, se você perder o acesso
        ao aplicativo. Eles não serão mostrados de novo.
      </p>
      <Item variant="muted" asChild>
        <ul className="grid grid-cols-2 gap-2 p-3 font-mono">
          {codes.map((code) => (
            <li key={code}>{code}</li>
          ))}
        </ul>
      </Item>
      <label className="flex items-center gap-2 text-sm">
        <Checkbox checked={saved} onCheckedChange={(checked) => setSaved(checked === true)} />
        Guardei os códigos em lugar seguro
      </label>
      <Button className="w-full" disabled={!saved} onClick={onDone}>
        Continuar
      </Button>
    </div>
  );
}
